import { useEffect, useState } from "react";
import { FileStack, Building2, Unlock, CalendarClock, IndianRupee, Clock, ShieldAlert, CheckCircle2 } from "lucide-react";
import { Area, AreaChart, Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { PageHeader, StatCard, Panel } from "@/components/common";
import { api, fmtMoney } from "@/lib/api";

const tip = { contentStyle: { background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 } };

export default function Analytics() {
  const [d, setD] = useState(null);

  useEffect(() => {
    api.get("/admin/analytics").then((r) => setD(r.data));
  }, []);

  return (
    <div>
      <PageHeader eyebrow="Super Admin" title="Platform analytics" subtitle="Leads, agencies, unlocks and revenue across the marketplace." />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
        <StatCard label="Total leads" value={d?.total_leads ?? "—"} icon={FileStack} hint={d && `${d.published_leads} published`} testId="admin-stat-total-leads" />
        <StatCard label="Agencies" value={d?.total_agencies ?? "—"} icon={Building2} tone="sky" hint={d && `${d.active_agencies} active`} testId="admin-stat-agencies" delay={50} />
        <StatCard label="Unlocked leads" value={d?.total_unlocks ?? "—"} icon={Unlock} tone="emerald" hint={d && `${d.credit_unlocks} via credits`} testId="admin-stat-unlocks" delay={100} />
        <StatCard label="Active subscriptions" value={d?.active_subscriptions ?? "—"} icon={CalendarClock} tone="amber" testId="admin-stat-subscriptions" delay={150} />
        <StatCard label="Revenue (approved)" value={d ? fmtMoney(d.revenue) : "—"} icon={IndianRupee} tone="emerald" testId="admin-stat-revenue" delay={200} />
        <StatCard label="Pending payments" value={d?.pending_payments ?? "—"} icon={Clock} tone="amber" testId="admin-stat-pending-payments" delay={250} />
        <StatCard label="Awaiting verification" value={d?.pending_verification ?? "—"} icon={ShieldAlert} tone="rose" testId="admin-stat-pending-verification" delay={300} />
        <StatCard label="Published leads" value={d?.published_leads ?? "—"} icon={CheckCircle2} testId="admin-stat-published" delay={350} />
      </div>
      <div className="grid lg:grid-cols-[1.6fr_1fr] gap-6 mt-8">
        <Panel title="Revenue — last 14 days">
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={d?.revenue_series || []}>
                <defs><linearGradient id="rev" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.3} /><stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0} /></linearGradient></defs>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                <XAxis dataKey="date" tickFormatter={(v) => v.slice(5)} tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} width={50} />
                <Tooltip {...tip} formatter={(v) => fmtMoney(v)} />
                <Area type="monotone" dataKey="revenue" stroke="hsl(var(--primary))" strokeWidth={2} fill="url(#rev)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Panel>
        <Panel title="Published leads by category">
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={d?.categories || []} layout="vertical" margin={{ left: 10 }}>
                <XAxis type="number" hide allowDecimals={false} />
                <YAxis type="category" dataKey="category" width={120} tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
                <Tooltip {...tip} cursor={{ fill: "hsl(var(--muted))" }} />
                <Bar dataKey="count" fill="hsl(var(--chart-2))" radius={[0, 6, 6, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Panel>
      </div>
    </div>
  );
}
